<template>
  <div class="relative" ref="rootEl">
    <button
      class="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:text-navy-700 hover:bg-gray-100 transition-colors"
      title="Notifications"
      @click="togglePanel"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
      </svg>
      <span
        v-if="unreadCount > 0"
        class="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white"
      >{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <Teleport to="body">
    <Transition enter-active-class="transition ease-out duration-150" enter-from-class="opacity-0 -translate-y-1" leave-active-class="transition ease-in duration-100" leave-to-class="opacity-0 -translate-y-1">
      <div v-if="open" :style="dropdownStyle" class="fixed w-80 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden z-[100]">
        <div class="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-b from-slate-50 to-white">
          <p class="text-sm font-bold text-gray-800">Notifications</p>
          <NuxtLink to="/dashboard/notifications" class="text-[11px] text-navy-600 hover:underline" @click="onSeeAll">See all</NuxtLink>
        </div>

        <!-- Phone CTA -->
        <div v-if="!hasPhone" class="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-start gap-2.5">
          <svg class="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
          <div class="flex-1 min-w-0">
            <p class="text-[11px] text-amber-800 leading-snug">Add your phone number to receive notifications via WhatsApp</p>
            <button class="mt-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900 underline" @click="openPhoneModal">+ Add Number</button>
          </div>
        </div>

        <div class="max-h-96 overflow-y-auto">
          <p v-if="loading" class="px-4 py-6 text-xs text-gray-400 italic text-center">Loading…</p>
          <p v-else-if="!recent.length" class="px-4 py-6 text-xs text-gray-400 italic text-center">No notifications.</p>
          <button
            v-for="n in recent" :key="n.id"
            type="button"
            class="w-full text-left px-4 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50 flex items-start gap-2.5"
            :class="n.status === 'sent' ? 'bg-blue-50/40' : ''"
            @click="onClickItem(n)"
          >
            <span class="inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold text-white flex-shrink-0 mt-0.5" :class="n.priority === 'high' ? 'bg-red-500' : 'bg-blue-500'">
              {{ (n.title || n.category).charAt(0).toUpperCase() }}
            </span>
            <div class="flex-1 min-w-0">
              <p v-if="n.title" class="text-xs font-semibold text-gray-800 truncate">{{ n.title }}</p>
              <p class="text-[11px] text-gray-600 leading-snug whitespace-pre-line line-clamp-3">{{ n.message }}</p>
              <div class="flex items-center gap-1.5 mt-0.5">
                <span class="text-[9px] text-gray-400 uppercase">{{ n.category }}</span>
                <span class="text-gray-300 text-[9px]">·</span>
                <span class="text-[9px] text-gray-400">{{ formatDt(n.createdAt) }}</span>
                <span v-if="n.waStatus && n.waStatus !== 'none'" class="text-gray-300 text-[9px]">·</span>
                <span v-if="n.waStatus && n.waStatus !== 'none'" class="text-[9px] font-semibold" :class="waStatusColor(n.waStatus)">WA: {{ waStatusLabel(n.waStatus) }}</span>
              </div>
            </div>
            <span v-if="n.status === 'sent'" class="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 mt-2" title="Unread" />
          </button>
        </div>
      </div>
    </Transition>
    </Teleport>

    <!-- Phone Modal -->
    <Teleport to="body">
      <Transition enter-active-class="transition ease-out duration-200" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition ease-in duration-150" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="phoneModal.open" class="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" @click.self="closePhoneModal">
          <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div class="px-5 pt-4 pb-3 border-b border-gray-100">
              <h3 class="text-base font-bold text-gray-800">WhatsApp Number</h3>
              <p class="text-[11px] text-gray-500 mt-0.5">E.164 format. A leading 0 is replaced with +62 automatically.</p>
            </div>
            <div class="px-5 py-4 space-y-2">
              <input
                v-model="phoneModal.input"
                type="tel"
                inputmode="tel"
                placeholder="+6281234567890"
                class="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-navy-300"
                @input="onPhoneInput"
              />
              <p v-if="phoneModal.error" class="text-[11px] text-red-600">{{ phoneModal.error }}</p>
              <p v-else class="text-[11px] text-gray-400">Only digits and a leading + are saved.</p>
            </div>
            <div class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button class="text-xs px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-200 font-semibold" @click="closePhoneModal">Cancel</button>
              <button class="text-xs px-4 py-2 rounded-lg bg-navy-600 hover:bg-navy-700 text-white font-semibold disabled:opacity-50" :disabled="phoneModal.saving" @click="savePhone">{{ phoneModal.saving ? 'Saving…' : 'Save' }}</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
interface NotificationRow {
  id: number
  title: string
  message: string
  category: string
  priority: 'low' | 'normal' | 'high'
  status: 'draft' | 'waiting' | 'sent' | 'read'
  waStatus: 'none' | 'pending' | 'sent' | 'failed' | 'no_phone'
  referenceModel: string | null
  referenceId: number | null
  referenceUrl: string | null
  createdAt: string
}

const open = ref(false)
const loading = ref(false)
const unreadCount = ref(0)
const recent = ref<NotificationRow[]>([])
const rootEl = ref<HTMLElement | null>(null)
const hasPhone = ref(true) // optimistic until /me confirms

// Position computed from the bell button's bounding rect, so the dropdown
// can be Teleported to <body> and still anchor under the bell.
const dropdownStyle = ref<{ top: string; right: string }>({ top: '0px', right: '0px' })
function recomputeDropdownPos() {
  if (!rootEl.value) return
  const rect = rootEl.value.getBoundingClientRect()
  dropdownStyle.value = {
    top:   `${rect.bottom + 8}px`,
    right: `${Math.max(window.innerWidth - rect.right, 8)}px`,
  }
}

let pollTimer: any = null

const { user, fetch: refreshSession } = useUserSession()

function normalizePhoneClient(input: string): string {
  if (!input) return ''
  let s = input.replace(/[^0-9+]/g, '')
  if (!s) return ''
  const hasPlus = s.startsWith('+')
  s = s.replace(/\+/g, '')
  if (!s) return ''
  if (hasPlus)               return '+' + s
  if (s.startsWith('0'))     return '+62' + s.slice(1)
  if (s.startsWith('62'))    return '+' + s
  return '+' + s
}

async function fetchUnread() {
  try {
    const r = await $fetch<{ count: number }>('/api/notifications/unread-count')
    unreadCount.value = r.count
  } catch { /* ignore — unauth or offline */ }
}
async function fetchRecent() {
  loading.value = true
  try {
    recent.value = await $fetch<NotificationRow[]>('/api/notifications', {
      query: { scope: 'mine', limit: 10, status: 'sent,read' },
    })
  } catch { /* ignore */ } finally { loading.value = false }
}
async function syncMe() {
  try {
    const me = await $fetch<{ phoneNumber?: string }>('/api/auth/me')
    hasPhone.value = !!(me?.phoneNumber && me.phoneNumber.length > 5)
  } catch { /* ignore */ }
}

function togglePanel() {
  open.value = !open.value
  if (open.value) {
    fetchRecent()
    syncMe()
    nextTick(recomputeDropdownPos)
  }
}
function onClickAway(e: MouseEvent) {
  if (!open.value) return
  if (rootEl.value && !rootEl.value.contains(e.target as Node)) open.value = false
}

// "See all" — tandai semua sent → read, reset counter ke 0, lalu tutup
// dropdown. Navigasi ke /dashboard/notifications di-handle oleh NuxtLink.
async function onSeeAll() {
  open.value = false
  if (unreadCount.value > 0) {
    const before = unreadCount.value
    unreadCount.value = 0  // optimistic
    try {
      await $fetch('/api/notifications/mark-all-read', { method: 'POST' })
    } catch {
      unreadCount.value = before  // rollback kalau gagal
    }
  }
}

async function onClickItem(n: NotificationRow) {
  if (n.status === 'sent') {
    try {
      await $fetch(`/api/notifications/${n.id}`, { method: 'PATCH', body: { status: 'read' } })
      n.status = 'read'
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    } catch { /* ignore */ }
  }
  if (n.referenceUrl) {
    open.value = false
    navigateTo(n.referenceUrl, { external: !n.referenceUrl.startsWith('/') })
  }
}

function waStatusLabel(s: string) {
  return ({ pending: 'queued', sent: 'sent', failed: 'failed', no_phone: 'no phone' } as Record<string, string>)[s] ?? s
}
function waStatusColor(s: string) {
  if (s === 'sent')    return 'text-emerald-600'
  if (s === 'failed')  return 'text-red-600'
  if (s === 'pending') return 'text-amber-600'
  return 'text-gray-400'
}

const phoneModal = reactive({ open: false, input: '', saving: false, error: '' })
function openPhoneModal() {
  phoneModal.input = ''
  phoneModal.error = ''
  phoneModal.open = true
}
function closePhoneModal() { phoneModal.open = false }
function onPhoneInput(e: Event) {
  const t = e.target as HTMLInputElement
  const cleaned = normalizePhoneClient(t.value)
  if (cleaned !== t.value) {
    phoneModal.input = cleaned
    t.value = cleaned
  }
}
async function savePhone() {
  const v = normalizePhoneClient(phoneModal.input)
  if (!/^\+\d{6,20}$/.test(v)) {
    phoneModal.error = 'Invalid phone number format (e.g. +6281234567890)'
    return
  }
  phoneModal.saving = true
  phoneModal.error = ''
  try {
    await $fetch('/api/auth/me/phone', { method: 'PUT', body: { phoneNumber: v } })
    hasPhone.value = true
    await refreshSession()
    closePhoneModal()
  } catch (e: any) {
    phoneModal.error = e?.data?.message ?? 'Failed to save'
  } finally {
    phoneModal.saving = false
  }
}

function formatDt(s: string) {
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  const now = Date.now()
  const diff = (now - d.getTime()) / 1000
  if (diff < 60)     return 'just now'
  if (diff < 3600)   return `${Math.floor(diff / 60)}m`
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

onMounted(() => {
  fetchUnread()
  syncMe()
  document.addEventListener('click', onClickAway)
  window.addEventListener('resize', recomputeDropdownPos)
  window.addEventListener('scroll', recomputeDropdownPos, true)
  pollTimer = setInterval(fetchUnread, 30_000)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onClickAway)
  window.removeEventListener('resize', recomputeDropdownPos)
  window.removeEventListener('scroll', recomputeDropdownPos, true)
  if (pollTimer) clearInterval(pollTimer)
})
</script>
